﻿import { KbVector } from '@models/classes/primitives/kb-vector';
import { MaterialService } from '@models/services/material-service';
import { Enumerable, IgnoreChanges } from '../decorators';
import { Inject } from '../decorators/inject-decorator';
import { Kb3dModel } from '../decorators/kb3d-decorator';
import { eAlphaMode, eMaterialLocation, eMaterialWorkflow, eReflectionType } from '../enums';
import { IAdvancedMaterialNode } from '../kb3d-models';
import { KbColor } from '../primitives/kb-color';
import { Token } from '../token';
import { MaterialNode } from './material-node';
import { TextureLayer } from './texture-layer';

@Kb3dModel<AdvancedMaterialNode>({
    token: Token.AdvancedMaterialNode,
    onSerializing: (node, o) => {
        if (node.parentMaterialId == null) {
            return true;
        }

        // remove any textures that are from the parent material
        o.textures = o.textures?.filter(texture => !texture._external);

        // remove any properties from the serialization that are inherited from a global or library material
        const keys = Object.keys(o);
        for (const p of keys) {
            if (node._overriddenPropertiesSet && !node._overriddenPropertiesSet.has(p) && p !== 'textures') {
                delete (o as any)[p];
            }
        }
        if (node._overriddenPropertiesSet) {
            o.overriddenProperties = Array.from(node._overriddenPropertiesSet).map(val => val.toString());
        }

        return node.location == eMaterialLocation.local;
    },
    addedToChildArray: (node, child) => {
        if (child instanceof TextureLayer) {
            if (node._initialized) {
                child.init(node);
            }
        }
    },
    afterCreate: node => {
        node.overriddenProperties.forEach(val => node._overriddenPropertiesSet.add(val));

        node.calculateParents(); //parents need to be calculated again because the texture children don't exist yet the first time it is run
        node.refreshParentMaterial();
    },
})
export class AdvancedMaterialNode extends MaterialNode implements IAdvancedMaterialNode {
    constructor(@Inject(Token.MaterialService) _materialService: MaterialService) {
        super(_materialService);

        this._initialized = false;
    }

    protected _materials: AdvancedMaterialNode[];
    @Enumerable()
    get materials(): AdvancedMaterialNode[] {
        if (!this._materials) this._materials = [];
        return this._materials;
    }
    set materials(val) {
        this._materials = val;
    }

    @IgnoreChanges
    _initialized = false;

    guid: string;

    alpha: number;
    alphaMode?: eAlphaMode;

    workflowMode: eMaterialWorkflow;

    /**
     * Intensity of the direct lights e.g. the four lights available in your scene.
     * This impacts both the direct diffuse and specular highlights.
     */
    directIntensity: number;

    /**
     * Intensity of the emissive part of the material.
     * This helps controlling the emissive effect without modifying the emissive color.
     */
    emissiveIntensity: number;

    /**
     * Intensity of the environment e.g. how much the environment will light the object
     * either through harmonics for rough material or through the refelction for shiny ones.
     */
    environmentIntensity: number;

    /**
     * This is a special control allowing the reduction of the specular highlights coming from the
     * four lights of the scene. Those highlights may not be needed in full environment lighting.
     */
    specularIntensity: number;

    /**
     * AKA Occlusion Texture Intensity in other nomenclature.
     */
    ambientTextureStrength?: number;

    /**
     * Specifies the metallic scalar of the metallic/roughness workflow.
     * Can also be used to scale the metalness values of the metallic texture.
     */
    metallic: number;

    /**
     * Specifies the roughness scalar of the metallic/roughness workflow.
     * Can also be used to scale the roughness values of the metallic texture.
     */
    roughness: number;

    /**
     * The color of a material in ambient lighting.
     */
    ambientColor: KbColor;

    /**
     * AKA Diffuse Color in other nomenclature.
     */
    albedoColor: KbColor;

    /**
     * AKA Specular Color in other nomenclature.
     */
    specularColor: KbColor;

    /**
     * The color reflected from the material.
     */
    reflectionColor: KbColor;

    /**
     * The type of reflection or refraction being used used (Plane or probe)
     */
    reflectionType: eReflectionType;

    /**
     * AKA Glossiness in other nomenclature.
     */
    microSurface: number;

    /**
     * Specifies that the material will keep the specular highlights over a transparent surface (only the most limunous ones).
     * A car glass is a good exemple of that. When sun reflects on it you can not see what is behind.
     */
    useSpecularOverAlpha: boolean;

    /**
     * Specifies that the material will keeps the reflection highlights over a transparent surface (only the most limunous ones).
     * A car glass is a good exemple of that. When the street lights reflects on it you can not see what is behind.
     */
    useRadianceOverAlpha: boolean;

    /** Anisotropy settings */
    anisotropyEnabled: boolean;
    anisotropicDirection: KbVector;
    anisotropicIntensity: number;

    /** Clearcoat settings */
    clearcoatEnabled: boolean;
    clearcoatIntensity: number;
    clearcoatTintColor: KbColor;
    clearcoatTintEnabled: boolean;
    clearcoatTintIntensity: number;

    /** Refraction settings */
    subsurfaceRefractionEnabled: boolean;
    subsurfaceReflectionEnabled: boolean;
    subsurfaceRefractionIndex: number;
    subsurfaceRefractionIntensity: number;
    subsurfaceRefractionLinkTransparency: boolean;

    /** Translucency settings */
    subsurfaceTranslucencyEnabled: boolean;
    subsurfaceTranslucencyIntensity: number;

    /** Common subsurface settings shared between refraction and translucency */
    subsurfaceThickness: number;
    subsurfaceTintColor: KbColor;
    subsurfaceUseAlbedoForTintColor: boolean;
}
